import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { getToken, setToken, getStoredUser, setStoredUser } from "@/lib/zafaraApi";

const ShopContext = createContext(null);

export const ShopProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser());
  const [ready, setReady] = useState(false);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [unread, setUnread] = useState(0);
  const lastStatuses = useRef({}); // orderId -> last known status (for notif polling)

  // On mount: if we have a token, hydrate user from /me
  useEffect(() => {
    (async () => {
      const t = getToken();
      if (!t) { setReady(true); return; }
      try {
        const me = await api.me();
        setUser(me);
        setStoredUser(me);
      } catch {
        setToken(null);
        setStoredUser(null);
        setUser(null);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const refreshCart = useCallback(async () => {
    if (!getToken()) return;
    try {
      const c = await api.cartGet();
      setCart({ items: c.items || [], total: c.total || 0 });
    } catch {
      /* silent — will retry on next action */
    }
  }, []);

  // Load cart when user changes
  useEffect(() => { if (user) refreshCart(); else setCart({ items: [], total: 0 }); }, [user, refreshCart]);

  // Notifications & order-status polling every 30s while logged in
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const c = await api.notifUnreadCount();
        if (!cancelled) setUnread(c?.count || c?.unread || 0);
      } catch { /* silent */ }
      try {
        const orders = await api.ordersMine();
        const list = orders?.orders || orders || [];
        if (Array.isArray(list)) {
          list.forEach((o) => {
            const prev = lastStatuses.current[o.id];
            if (prev && prev !== o.status) {
              // Status changed — show a web notification via sonner
              const msgMap = {
                confirmed: "Commande confirmée ✅",
                assigned: "Livreur attribué 🛵",
                picked_up: "Colis récupéré 📦",
                in_delivery: "En cours de livraison 🚚",
                delivered: "Colis livré 🎉",
                cancelled: "Commande annulée ❌",
              };
              toast.success(msgMap[o.status] || `Nouveau statut : ${o.status}`, {
                description: `Commande #${(o.tracking_code || o.id).slice(0, 8).toUpperCase()}`,
              });
            }
            lastStatuses.current[o.id] = o.status;
          });
        }
      } catch { /* silent — polling continues */ }
    };
    tick();
    const iv = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  const login = async (email, password) => {
    const res = await api.login({ email, password });
    // API returns access_token or token — support both
    const t = res.access_token || res.token;
    if (!t) throw new Error("Réponse serveur inattendue.");
    setToken(t);
    const me = await api.me();
    setUser(me);
    setStoredUser(me);
    await refreshCart();
    return me;
  };

  const register = async (payload) => {
    // Ensure customer role + terms accepted
    const body = { role: "customer", accepted_terms: true, ...payload };
    await api.register(body);
    return login(body.email, body.password);
  };

  const logout = () => {
    setToken(null);
    setStoredUser(null);
    setUser(null);
    setCart({ items: [], total: 0 });
    setUnread(0);
  };

  const addToCart = async (product_id, quantity = 1, size = null) => {
    if (!getToken()) throw Object.assign(new Error("Not logged in"), { code: "NO_AUTH" });
    const payload = { product_id, quantity };
    if (size) payload.size = size;
    await api.cartAdd(payload);
    await refreshCart();
  };

  const updateCartItem = async (item_id, quantity) => {
    await api.cartUpdate(item_id, { quantity });
    await refreshCart();
  };
  const removeCartItem = async (item_id) => {
    await api.cartRemove(item_id);
    await refreshCart();
  };

  const value = {
    user, ready, cart, unread,
    login, register, logout,
    refreshCart, addToCart, updateCartItem, removeCartItem,
  };
  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

export const useShop = () => {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used within ShopProvider");
  return ctx;
};
